import {
  Modal,
  Box,
  Typography,
  Button,
  styled,
  keyframes,
  IconButton,
  Fab,
} from '@mui/material';
import {
  Microphone,
  X,
  Robot,
  SpeakerHigh,
  Waveform,
  Brain,
  Minus,
} from '@phosphor-icons/react';
import { useEffect, useState, useCallback } from 'react';

interface EndAssistantCallModalProps {
  open: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onRestore: () => void;
  isConnecting: boolean;
  isConnected: boolean;
  isMinimized: boolean;
  ultravoxStatus: string;
  agentName?: string;
}

const DotContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginBottom: '16px',
});

const Dot = styled(Box)({
  width: '12px',
  height: '12px',
  backgroundColor: '#D1D5DB',
  borderRadius: '50%',
  animation: 'bounce 1.4s infinite ease-in-out',
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-12px)',
    },
  },
});

const wave = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`;

const WaveCircle = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  border: '2px solid',
  animation: `${wave} 1.5s infinite`,
});

const PulseCircle = styled(Box)({
  position: 'absolute',
  top: -10,
  left: -10,
  width: 'calc(100% + 20px)',
  height: 'calc(100% + 20px)',
  borderRadius: '50%',
  backgroundColor: 'rgba(90, 76, 230, 0.1)',
  animation: `${pulse} 2s infinite`,
});

const IconWrapper = styled(Box)({
  position: 'relative',
  width: '120px',
  height: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const IconCircle = styled(Box, {
  shouldForwardProp: prop => prop !== 'statusType' && prop !== 'isActive',
})<{ statusType?: string; isActive?: boolean }>(({ statusType, isActive }) => {
  let backgroundColor = '#5A4CE6'; // default

  switch (statusType) {
    case 'speaking':
      backgroundColor = '#2ECC71'; // Green for bot speaking
      break;
    case 'listening':
      backgroundColor = '#3498DB'; // Blue for listening
      break;
    case 'thinking':
      backgroundColor = '#F39C12'; // Orange for thinking
      break;
    default:
      backgroundColor = '#5A4CE6'; // Purple for idle/default
  }

  return {
    width: '120px',
    height: '120px',
    backgroundColor,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
    color: '#FFFFFF',
    transition: 'all 0.3s ease',
    boxShadow: isActive
      ? `0 0 30px ${backgroundColor}40`
      : '0 4px 20px rgba(0, 0, 0, 0.1)',
    transform: isActive ? 'scale(1.05)' : 'scale(1)',
  };
});

const StatusIndicator = styled(Box, {
  shouldForwardProp: prop => prop !== 'statusType',
})<{ statusType?: string }>(({ statusType }) => {
  let backgroundColor = '#3498DB';

  switch (statusType) {
    case 'speaking':
      backgroundColor = '#27AE60';
      break;
    case 'listening':
      backgroundColor = '#2980B9';
      break;
    case 'thinking':
      backgroundColor = '#E67E22';
      break;
    default:
      backgroundColor = '#3498DB';
  }

  return {
    position: 'absolute',
    bottom: 8,
    right: 8,
    width: '24px',
    height: '24px',
    backgroundColor,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontSize: '12px',
    zIndex: 2,
    border: '2px solid #FFFFFF',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  };
});

// Updated MinimizedWidget with draggable functionality
const MinimizedWidget = styled(Fab, {
  shouldForwardProp: prop =>
    prop !== 'statusType' && prop !== 'isActive' && prop !== 'isDragging',
})<{ statusType?: string; isActive?: boolean; isDragging?: boolean }>(({
  statusType,
  isActive,
  isDragging,
}) => {
  let backgroundColor = '#5A4CE6';

  switch (statusType) {
    case 'speaking':
      backgroundColor = '#2ECC71';
      break;
    case 'listening':
      backgroundColor = '#3498DB';
      break;
    case 'thinking':
      backgroundColor = '#F39C12';
      break;
    default:
      backgroundColor = '#5A4CE6';
  }

  return {
    position: 'fixed',
    width: '64px',
    height: '64px',
    backgroundColor,
    color: '#FFFFFF',
    boxShadow: isDragging
      ? '0 12px 32px rgba(0, 0, 0, 0.25)'
      : '0 8px 24px rgba(0, 0, 0, 0.15)',
    zIndex: 1300,
    animation: isActive && !isDragging ? `${bounce} 2s infinite` : 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    transform: isDragging ? 'scale(1.1)' : 'scale(1)',
    transition: isDragging ? 'none' : 'all 0.2s ease',
    '&:hover': {
      backgroundColor,
      transform: isDragging ? 'scale(1.1)' : 'scale(1.05)',
    },
    '&:active': {
      transform: 'scale(1.1)',
    },
    '& .MuiTouchRipple-root': {
      color: '#FFFFFF',
    },
  };
});

const MinimizedWaveCircle = styled(Box)({
  position: 'absolute',
  top: -8,
  left: -8,
  width: 'calc(100% + 16px)',
  height: 'calc(100% + 16px)',
  borderRadius: '50%',
  border: '2px solid',
  animation: `${wave} 1.5s infinite`,
  pointerEvents: 'none',
});

const modalStyle = {
  width: '420px',
  minHeight: '480px',
  display: 'flex',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
  flexShrink: 0,
  borderRadius: '16px',
  bgcolor: '#FFF',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FC 100%)',
};

const EndAssistantCallModal = ({
  open,
  onClose,
  onMinimize,
  onRestore,
  isConnecting,
  isConnected,
  isMinimized,
  ultravoxStatus,
  agentName,
}: EndAssistantCallModalProps) => {
  // Draggable state
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('callWidgetPosition');
    return saved
      ? JSON.parse(saved)
      : { x: window.innerWidth - 88, y: window.innerHeight - 88 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false); // Track if widget actually moved

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('callWidgetPosition', JSON.stringify(position));
  }, [position]);

  // Reset position if it's off-screen (window resize)
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev: { x: number; y: number }) => ({
        x: Math.min(prev.x, window.innerWidth - 64),
        y: Math.min(prev.y, window.innerHeight - 64),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [, setIsMinimizedState] = useState(false);

  useEffect(() => {
    if (open && isConnecting) {
      const timer = setTimeout(() => {
        if (isConnecting) {
          onClose();
        }
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [open, isConnecting, onClose]);

  // Add ESC key listener to end call (but not when minimized)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open && !isMinimized) {
        onClose();
      }
    };

    if (open && !isMinimized) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, isMinimized]);

  // Reset minimized state when modal is closed
  useEffect(() => {
    if (!open) {
      setIsMinimizedState(false);
    }
  }, [open]);

  // const handleMinimize = () => {
  //   setIsMinimizedState(true);
  // };

  // const handleRestore = () => {
  //   setIsMinimizedState(false);
  // };

  const getStatusText = () => {
    switch (ultravoxStatus) {
      case 'connecting':
        return `Connecting to ${agentName}...`;
      case 'speaking':
        return `${agentName} is speaking`;
      case 'listening':
        return 'You can speak now';
      case 'thinking':
        return `${agentName} is thinking...`;
      case 'idle':
        return `Connected to ${agentName}`;
      case 'disconnecting':
        return 'Ending call...';
      case 'disconnected':
        return 'Call ended';
      default:
        return 'Call status unknown';
    }
  };

  const getIcon = () => {
    switch (ultravoxStatus) {
      case 'speaking':
        return <Robot size={40} weight="bold" />;
      case 'listening':
        return <Microphone size={40} weight="bold" />;
      case 'thinking':
        return <Brain size={40} weight="bold" />;
      case 'idle':
        return <SpeakerHigh size={40} weight="bold" />;
      default:
        return <Microphone size={40} weight="bold" />;
    }
  };

  const getMinimizedIcon = () => {
    switch (ultravoxStatus) {
      case 'speaking':
        return <Robot size={24} weight="bold" />;
      case 'listening':
        return <Microphone size={24} weight="bold" />;
      case 'thinking':
        return <Brain size={24} weight="bold" />;
      case 'idle':
        return <SpeakerHigh size={24} weight="bold" />;
      default:
        return <Microphone size={24} weight="bold" />;
    }
  };

  const getStatusIndicatorIcon = () => {
    switch (ultravoxStatus) {
      case 'speaking':
        return <Waveform size={12} />;
      case 'listening':
        return <Microphone size={12} />;
      case 'thinking':
        return <Brain size={10} />;
      default:
        return <SpeakerHigh size={12} />;
    }
  };

  const getWaveColor = () => {
    switch (ultravoxStatus) {
      case 'speaking':
        return '#2ECC71';
      case 'listening':
        return '#3498DB';
      case 'thinking':
        return '#F39C12';
      default:
        return '#5A4CE6';
    }
  };

  const getHelperText = () => {
    switch (ultravoxStatus) {
      case 'listening':
        return 'The bot is listening to you';
      case 'speaking':
        return 'The bot is speaking';
      case 'thinking':
        return 'Processing your request...';
      case 'idle':
        return 'Connection established';
      default:
        return '';
    }
  };

  const isActive = ['speaking', 'listening', 'thinking'].includes(
    ultravoxStatus
  );
  const showWaves = ['speaking', 'listening'].includes(ultravoxStatus);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    setIsDragging(true);
    setHasMoved(false); // Reset movement flag

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setDragStart({
      x: clientX - position.x,
      y: clientY - position.y,
    });
  };

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const newX = Math.max(
        0,
        Math.min(clientX - dragStart.x, window.innerWidth - 64)
      );
      const newY = Math.max(
        0,
        Math.min(clientY - dragStart.y, window.innerHeight - 64)
      );

      // Check if position actually changed
      if (newX !== position.x || newY !== position.y) {
        setHasMoved(true); // Mark that we've moved
        setPosition({ x: newX, y: newY });
      }
    },
    [isDragging, dragStart.x, dragStart.y, position.x, position.y]
  );

  const handleDragEnd = () => {
    setIsDragging(false);
    // Don't reset hasMoved here - we need it for the click handler
  };

  // Add global mouse/touch event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchMove = (e: TouchEvent) => handleDragMove(e);
      const handleTouchEnd = () => handleDragEnd();

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleDragMove]);

  // Handle click vs drag
  const handleWidgetInteraction = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only restore if it's a click (not a drag) and widget didn't move
    if (!isDragging && !hasMoved) {
      onRestore();
    }

    // Reset movement flag after handling the interaction
    setTimeout(() => setHasMoved(false), 100);
  };

  // Show minimized widget when minimized
  if (open && isMinimized) {
    return (
      <MinimizedWidget
        statusType={ultravoxStatus}
        isActive={showWaves}
        isDragging={isDragging}
        onClick={handleWidgetInteraction}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        aria-label="Restore call window (drag to move)"
        style={{
          left: position.x,
          top: position.y,
          right: 'auto',
          bottom: 'auto',
        }}
      >
        {getMinimizedIcon()}
        {showWaves && !isDragging && (
          <>
            <MinimizedWaveCircle
              sx={{
                borderColor: getWaveColor(),
                animationDelay: '0s',
              }}
            />
            <MinimizedWaveCircle
              sx={{
                borderColor: getWaveColor(),
                animationDelay: '0.5s',
              }}
            />
          </>
        )}
      </MinimizedWidget>
    );
  }

  return (
    <Modal
      open={open && !isMinimized}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          onMinimize();
        } else {
          onClose();
        }
      }}
      aria-labelledby="call-modal-title"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <Box sx={modalStyle}>
        <Box
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            display: 'flex',
            gap: 1,
          }}
        >
          <IconButton
            onClick={onMinimize}
            sx={{
              color: '#6B6F80',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
            aria-label="Minimize call window"
          >
            <Minus size={20} />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#6B6F80',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
            aria-label="End call"
          >
            <X size={20} />
          </IconButton>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            padding: '32px 24px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            flex: 1,
          }}
        >
          {ultravoxStatus === 'connecting' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '24px',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              <DotContainer>
                {[0, 1, 2, 3].map(i => (
                  <Dot
                    key={i}
                    sx={{
                      animationDelay: `${i * 150}ms`,
                      backgroundColor: '#5A4CE6',
                    }}
                  />
                ))}
              </DotContainer>
              <Typography
                variant="h6"
                sx={{
                  color: '#2C3E50',
                  fontSize: '18px',
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                {getStatusText()}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#7F8C8D',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              >
                Please wait while we establish the connection
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '24px',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              <IconWrapper>
                {ultravoxStatus === 'listening' && <PulseCircle />}

                <IconCircle statusType={ultravoxStatus} isActive={isActive}>
                  {getIcon()}
                  <StatusIndicator statusType={ultravoxStatus}>
                    {getStatusIndicatorIcon()}
                  </StatusIndicator>
                </IconCircle>

                {showWaves && (
                  <>
                    <WaveCircle
                      sx={{
                        animationDelay: '0s',
                        borderColor: getWaveColor(),
                      }}
                    />
                    <WaveCircle
                      sx={{
                        animationDelay: '0.5s',
                        borderColor: getWaveColor(),
                      }}
                    />
                    <WaveCircle
                      sx={{
                        animationDelay: '1s',
                        borderColor: getWaveColor(),
                      }}
                    />
                  </>
                )}
              </IconWrapper>

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#2C3E50',
                    fontSize: '18px',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {getStatusText()}
                </Typography>
                {getHelperText() && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#7F8C8D',
                      fontSize: '14px',
                    }}
                  >
                    {getHelperText()}
                  </Typography>
                )}
              </Box>

              {/* Call status indicator */}
              {isConnected && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 16px',
                    backgroundColor: 'rgba(90, 76, 230, 0.1)',
                    borderRadius: '20px',
                    border: '1px solid rgba(90, 76, 230, 0.2)',
                  }}
                >
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#27AE60',
                      borderRadius: '50%',
                      animation:
                        ultravoxStatus === 'idle'
                          ? `${pulse} 2s infinite`
                          : 'none',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#5A4CE6',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Call Active
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            padding: '20px 24px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            alignSelf: 'stretch',
            borderTop: '1px solid rgba(220, 224, 231, 0.5)',
            backgroundColor: 'rgba(248, 249, 252, 0.8)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isConnecting}
            sx={{
              display: 'flex',
              padding: '12px 32px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)',
              minWidth: '140px',
              '&:hover': {
                background: 'linear-gradient(135deg, #C0392B 0%, #A93226 100%)',
                boxShadow: '0 6px 16px rgba(231, 76, 60, 0.4)',
              },
              '&:disabled': {
                background: '#BDC3C7',
                boxShadow: 'none',
              },
            }}
          >
            End Call
          </Button>

          {!isConnecting && (
            <Typography
              variant="caption"
              sx={{
                color: '#95A5A6',
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              Minimize or press Esc to end call
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default EndAssistantCallModal;
